import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import type { OwnerSessionExtra } from "@teamna/shared";
import { useDecideSessionExtra, useOwnerSessionExtras } from "@teamna/web-client/hooks/use-coach-participants.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { formatDateTime } from "@teamna/web-client/lib/format.ts";
import { messageForError } from "../components/CodeFlow.tsx";
import { Avatar, Button, Checkbox, ErrorState, SkeletonList, showToast } from "../components/index.ts";
import { StaleNotice } from "./todo/stale-notice.tsx";

const DAYS = ["Sundays", "Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays"];

/**
 * "Decide N sessions", a row of the owner's To do list on Home (strategy repo:
 * teamna/prototypes/someone-else-came.html, then owner-answers.html concept D). Each swimmer who
 * came and was not booked: "Take 1 session" from the family's package, or "Let it go". A name with
 * no parent is linked first. Teamna takes nothing until the owner decides, and a visit nobody decides
 * for 7 days drops off.
 */
export function DecideSessionsList({ businessId, timezone }: { businessId: string; timezone: string }) {
  const { label } = useLabels();
  const list = useOwnerSessionExtras(businessId);
  const items = list.data?.items ?? [];
  if (!list.data) return list.isLoading ? <SkeletonList rows={2} /> : <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  const stale = list.isError ? <StaleNotice onRetry={() => void list.refetch()} /> : null;
  if (items.length === 0) {
    return (
      <div className="flex flex-col gap-2">
        {stale}
        <p className="text-[13.5px] text-ink-2">Nothing to decide.</p>
      </div>
    );
  }
  return (
    <section aria-label="Sessions to decide" className="flex flex-col gap-2">
      {stale}
      <p className="text-[13.5px] text-ink-2">
        {label("participant", { plural: true, capital: true })} who came and were not booked. Teamna takes nothing until you decide.
      </p>
      {items.map((item) => (
        <DecideRow key={item.id} businessId={businessId} item={item} timezone={timezone} />
      ))}
    </section>
  );
}

function DecideRow({ businessId, item, timezone }: { businessId: string; item: OwnerSessionExtra; timezone: string }) {
  const { label } = useLabels();
  const navigate = useNavigate();
  const decide = useDecideSessionExtra(businessId);
  const [addDay, setAddDay] = useState(false);
  const first = item.name.split(" ")[0] ?? item.name;
  const run = (money: "take" | "free") =>
    decide.mutate(
      { id: item.id, money, ...(addDay ? { addWeekday: true } : {}) },
      {
        onSuccess: () => showToast(money === "take" ? `1 session taken for ${first}.` : `No session taken for ${first}.`),
        onError: (error) => showToast(messageForError(error), { tone: "info" }),
      },
    );

  return (
    <article className="flex flex-col gap-2 rounded-card border border-line bg-paper px-4 py-3" aria-label={item.name}>
      <div className="flex items-start gap-3">
        <Avatar name={item.name} photoUrl={null} size={32} />
        <div className="min-w-0 flex-1 text-[14px]">
          <p className="text-ink">
            <b>{item.name}</b> came to {item.groupName}, {formatDateTime(item.startsAt, timezone)}
            {item.staffName ? ` with ${item.staffName}` : ""}. Not booked.
          </p>
          {item.kind === "participant" ? (
            <p className="text-[12.5px] text-ink-2">
              {[item.where, item.familyName ? `${label("member", { capital: true })}: ${item.familyName}` : null].filter(Boolean).join(". ")}.
            </p>
          ) : null}
        </div>
      </div>
      {item.kind === "waiting" ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-[13px] text-ink-2">
            {first} has no {label("member")} yet. Link a {label("member")} first.
          </p>
          <Button size="sm" onClick={() => void navigate({ to: "/owner/new-participants" })}>
            Link a {label("member")}
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {item.canTake ? (
            <Button size="sm" loading={decide.isPending} onClick={() => run("take")}>
              Take 1 session
            </Button>
          ) : (
            <span className="text-[13px] text-ink-2">No package with a session left for this {label("group")}. Nothing to take.</span>
          )}
          <Button size="sm" variant="secondary" loading={decide.isPending} onClick={() => run("free")}>
            Let it go
          </Button>
          {!item.onWeekday ? (
            <Checkbox label={`Also add ${DAYS[item.weekday]} to ${first}'s days`} checked={addDay} onChange={setAddDay} size="compact" />
          ) : null}
        </div>
      )}
    </article>
  );
}
